import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Link } from "react-router-dom";
import { Flame, Activity, Apple, ArrowRight, Sparkles, Target, Play } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import MuscleDiagram from "../components/MuscleDiagram";
import MicroWorkoutModal from "../components/MicroWorkoutModal";
import TrialBanner from "../components/TrialBanner";
import DailyChecklist from "../components/DailyChecklist";
import MacroSplitModal from "../components/MacroSplitModal";
import BadgeStrip from "../components/BadgeStrip";
import { muscleGroupLabels } from "../data/exerciseLibrary";

const MACRO_COLORS = {
  dark:  { protein: "#6B9BFF", carbs: "#7C3AED", fat: "#FF3B30" },
  light: { protein: "#b76e79", carbs: "#c99a86", fat: "#8b4a52" },
};

const Card = ({ children, className = "", ...rest }) => (
  <div className={`border border-white/10 bg-[#121212] p-6 ${className}`} {...rest}>
    {children}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const macroPalette = MACRO_COLORS[theme] || MACRO_COLORS.dark;
  const [today, setToday] = useState(null);
  const [plan, setPlan] = useState(null);
  const [muscleCounts, setMuscleCounts] = useState({});
  const [activeGoal, setActiveGoal] = useState(null);
  const [microMuscle, setMicroMuscle] = useState(null);
  const [macroModalOpen, setMacroModalOpen] = useState(false);

  useEffect(() => {
    api.get("/food/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/workouts/current").then((r) => setPlan(r.data?.plan || null)).catch(() => {});
    api.get("/workouts/weekly-muscle-summary")
      .then((r) => setMuscleCounts(r.data?.counts || {}))
      .catch(() => {});
    api.get("/goals")
      .then((r) => {
        const weight = (r.data?.goals || []).find(
          (g) => g.type === "weight" && g.recommended_kcal
        );
        setActiveGoal(weight || null);
      })
      .catch(() => {});
  }, []);

  const target = activeGoal?.recommended_kcal || user?.daily_calorie_target || 2200;
  const macrosTarget = activeGoal?.macros || null;
  const consumed = today?.totals?.calories || 0;
  const remaining = Math.max(0, target - consumed);
  const progress = Math.min(100, Math.round((consumed / target) * 100));

  const macros = today?.totals || { protein_g: 0, carbs_g: 0, fat_g: 0 };
  const pieData = [
    { name: "Protein", value: Math.max(0.0001, macros.protein_g), color: macroPalette.protein, target: macrosTarget?.protein_g },
    { name: "Carbs", value: Math.max(0.0001, macros.carbs_g), color: macroPalette.carbs, target: macrosTarget?.carbs_g },
    { name: "Fat", value: Math.max(0.0001, macros.fat_g), color: macroPalette.fat, target: macrosTarget?.fat_g },
  ];

  const todayName = new Date().toLocaleDateString("en-US", { weekday: "long" });
  const todayIdx = plan?.days?.findIndex((d) =>
    d.day?.toLowerCase().includes(todayName.toLowerCase())
  );
  const todayWorkout = todayIdx >= 0 ? plan?.days?.[todayIdx] : null;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-up" data-testid="dashboard-page">
      <TrialBanner />
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow text-[#6B9BFF] mb-2">Dashboard</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-none">
            Welcome, {user?.name?.split(" ")[0] || "Athlete"}
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {todayWorkout && todayWorkout.exercises?.length > 0 && (
            <Link
              to={`/session/${todayIdx}`}
              className="bg-[#6B9BFF] text-black font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-white transition-colors inline-flex items-center gap-2"
              data-testid="start-todays-workout"
            >
              <Play className="w-4 h-4" /> Start today's workout
            </Link>
          )}
          <div className="text-white/50 text-sm">
            {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="md:col-span-3">
          <DailyChecklist
            plan={plan}
            calorieTarget={target}
            foodTotals={today?.totals || null}
          />
        </div>
        <div className="md:col-span-3">
          <BadgeStrip compact />
        </div>
        <Card data-testid="card-calories">
          <div className="flex items-center justify-between mb-4">
            <div className="label-eyebrow text-white/50">Calories Today</div>
            <Flame className="w-4 h-4 text-[#6B9BFF]" />
          </div>
          <div className="font-display text-5xl">{consumed}</div>
          <div className="text-sm text-white/50 mt-1">of {target} kcal target</div>
          <div className="mt-4 h-1.5 bg-white/10">
            <div
              className="h-full bg-[#6B9BFF] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-xs text-white/40 mt-2">{remaining} kcal remaining</div>
        </Card>

        <Card data-testid="card-protein">
          <div className="flex items-center justify-between mb-4">
            <div className="label-eyebrow text-white/50">Protein</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMacroModalOpen(true)}
                className="text-[10px] uppercase tracking-widest font-bold text-white/40 hover:text-[#6B9BFF]"
                data-testid="dashboard-edit-macros"
                title="Adjust macro split"
              >
                Edit
              </button>
              <Apple className="w-4 h-4 text-[#6B9BFF]" />
            </div>
          </div>
          <div className="font-display text-5xl">
            {Math.round(macros.protein_g)}
            {macrosTarget?.protein_g ? (
              <span className="text-2xl text-white/40"> / {macrosTarget.protein_g}g</span>
            ) : (
              <span className="text-2xl text-white/40">g</span>
            )}
          </div>
          <div className="text-sm text-white/50 mt-1">
            Carbs {Math.round(macros.carbs_g)}{macrosTarget?.carbs_g ? `/${macrosTarget.carbs_g}` : ""}g · Fat {Math.round(macros.fat_g)}{macrosTarget?.fat_g ? `/${macrosTarget.fat_g}` : ""}g
          </div>
          {macrosTarget?.protein_g && (
            <div className="mt-4 h-1.5 bg-white/10" data-testid="protein-progress">
              <div
                className="h-full bg-[#6B9BFF] transition-all"
                style={{ width: `${Math.min(100, Math.round((macros.protein_g / macrosTarget.protein_g) * 100))}%` }}
              />
            </div>
          )}
        </Card>

        <Card data-testid="card-workout-today">
          <div className="flex items-center justify-between mb-4">
            <div className="label-eyebrow text-white/50">Today's Session</div>
            <Activity className="w-4 h-4 text-[#6B9BFF]" />
          </div>
          {todayWorkout ? (
            <>
              <div className="font-display text-2xl leading-tight">{todayWorkout.focus}</div>
              <div className="text-sm text-white/50 mt-1">
                {todayWorkout.exercises?.length || 0} exercises · {todayWorkout.duration_min || 45} min
              </div>
              <Link
                to="/app/workout"
                className="inline-flex items-center gap-2 text-[#6B9BFF] text-xs uppercase tracking-widest font-bold mt-4 hover:underline"
                data-testid="dashboard-view-workout"
              >
                View plan <ArrowRight className="w-3 h-3" />
              </Link>
            </>
          ) : (
            <>
              <div className="font-display text-2xl leading-tight">No plan yet</div>
              <div className="text-sm text-white/50 mt-1">Generate your AI workout</div>
              <Link
                to="/app/workout"
                className="inline-flex items-center gap-2 text-[#6B9BFF] text-xs uppercase tracking-widest font-bold mt-4 hover:underline"
                data-testid="dashboard-generate-workout"
              >
                Generate now <ArrowRight className="w-3 h-3" />
              </Link>
            </>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="label-eyebrow text-white/50 mb-1">Macros breakdown</div>
              <div className="font-display text-2xl">Today's split</div>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  stroke={theme === "light" ? "#faf5ec" : "#0a0a0a"}
                  strokeWidth={3}
                >
                  {pieData.map((d) => (
                    <Cell key={d.name} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={
                    theme === "light"
                      ? { background: "#faf5ec", border: "1px solid rgba(42,31,24,0.18)", color: "#2a1f18" }
                      : { background: "#121212", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }
                  }
                  formatter={(val, name) => [`${Math.round(val)}g`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-6 justify-center mt-2 text-sm flex-wrap">
            {pieData.map((p) => (
              <div key={p.name} className="flex items-center gap-2" data-testid={`macro-legend-${p.name.toLowerCase()}`}>
                <span className="w-3 h-3 inline-block" style={{ background: p.color }} />
                <span className="text-white/70">{p.name}</span>
                {p.target ? (
                  <span className="text-white/40 text-xs">
                    {Math.round(p.value < 0.01 ? 0 : p.value)}/{p.target}g
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </Card>

        <div className="tracing-border p-6">
          <div className="label-eyebrow text-[#6B9BFF] mb-3 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> AI Quick Actions
          </div>
          <div className="space-y-3">
            <Link
              to="/app/nutrition"
              className="block border border-white/10 hover:border-[#6B9BFF] p-4 transition-colors group"
              data-testid="quick-log-food"
            >
              <div className="font-display text-lg">Log a meal</div>
              <div className="text-xs text-white/50 mt-1">Manual or describe in text</div>
            </Link>
            <Link
              to="/app/workout"
              className="block border border-white/10 hover:border-[#6B9BFF] p-4 transition-colors"
              data-testid="quick-workout"
            >
              <div className="font-display text-lg">Generate workout</div>
              <div className="text-xs text-white/50 mt-1">AI-built weekly plan</div>
            </Link>
            <Link
              to="/app/progress"
              className="block border border-white/10 hover:border-[#6B9BFF] p-4 transition-colors"
              data-testid="quick-weight"
            >
              <div className="font-display text-lg">Log weight</div>
              <div className="text-xs text-white/50 mt-1">Track your trend</div>
            </Link>
          </div>
        </div>
      </div>

      {/* Weekly Muscle Heatmap */}
      <div className="mt-6 border border-white/10 bg-[#121212] p-6" data-testid="muscle-heatmap-card">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="label-eyebrow text-[#6B9BFF] mb-2 flex items-center gap-2">
              <Target className="w-3.5 h-3.5" /> This week's training
            </div>
            <div className="font-display text-2xl">Muscle heatmap</div>
            <p className="text-white/60 text-sm mt-2 max-w-md">
              Brighter zones = more sets logged this week. Tap any muscle to get a 15-min AI micro-workout for it.
            </p>
          </div>
          <Link
            to="/app/library"
            className="text-xs uppercase tracking-widest font-bold text-[#6B9BFF] hover:underline"
            data-testid="heatmap-library-link"
          >
            Browse library →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="flex justify-center">
            <MuscleDiagram
              intensities={(() => {
                const max = Math.max(1, ...Object.values(muscleCounts));
                const out = {};
                Object.keys(muscleCounts).forEach((k) => {
                  out[k] = Math.min(1, muscleCounts[k] / max);
                });
                return out;
              })()}
              size={280}
              onMuscleClick={(m) => setMicroMuscle(m)}
            />
          </div>
          <div>
            <div className="grid grid-cols-2 gap-2">
              {["chest", "back", "shoulders", "biceps", "triceps", "core",
                "quads", "hamstrings", "glutes", "calves"].map((m) => {
                const c = muscleCounts[m] || 0;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMicroMuscle(m)}
                    className={`group flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2 text-xs px-3 py-2 border text-left hover:border-[#6B9BFF] hover:bg-[#6B9BFF]/5 transition-colors ${
                      c > 0 ? "border-[#6B9BFF]/30" : "border-white/5"
                    }`}
                    data-testid={`heatmap-row-${m}`}
                    title="Tap for an AI micro-workout targeting this muscle"
                  >
                    <span className="text-white/70 uppercase tracking-wider group-hover:text-white truncate">{muscleGroupLabels[m]}</span>
                    <span className="flex items-center gap-2">
                      <span className={`whitespace-nowrap ${c > 0 ? "text-[#6B9BFF] font-bold" : "text-white/30"}`}>
                        {c} {c === 1 ? "set" : "sets"}
                      </span>
                      <Sparkles className="w-3 h-3 text-white/20 group-hover:text-[#6B9BFF]" />
                    </span>
                  </button>
                );
              })}
            </div>
            {Object.keys(muscleCounts).length === 0 && (
              <div className="mt-4 text-xs text-white/40 italic">
                Log a set in your <Link to="/app/workout" className="text-[#6B9BFF] underline">workout</Link> to start the heatmap.
              </div>
            )}
          </div>
        </div>
      </div>

      <MicroWorkoutModal muscleGroup={microMuscle} onClose={() => setMicroMuscle(null)} />
      <MacroSplitModal open={macroModalOpen} onOpenChange={setMacroModalOpen} kcalTarget={target} />
    </div>
  );
}
